// Hook: useTreeInteraction.js
// Purpose: Author-mode tree keyboard, hover, Locate and native drag/drop interaction. The tree owns only slot choice and
//          the refusal check; every graph write is the shared useGraphWiring canvas code (onConnect, handleEdgesChange, deleteNode).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { LINK_REFUSAL_TEXT } from './useGraphWiring';
import { resolveConditionAttachHandle, resolveTargetHandle } from '../utils/operandLinking';

const isTextInput = (target) => {
  const tag = target?.tagName?.toLowerCase();
  return ['input', 'textarea', 'select'].includes(tag) || target?.isContentEditable;
};

const parseValue = (value) => {
  try { return JSON.parse(value || ''); } catch (_) { return null; }
};

export const TREE_NODE_MIME = 'application/x-deetrade-tree-node';

export const parseToolboxDropItem = (dataTransfer) => {
  const type = dataTransfer?.getData('application/reactflow');
  const data = parseValue(dataTransfer?.getData('application/nodeData'));
  return type && data ? { type, data } : null;
};

const flattenRows = (sections) => (sections || []).flatMap(({ rows = [] }) => rows);
//--> rendered rows only (descendants of collapsed rows excluded), so the roving tab stop is always on screen (B5)
const flattenVisibleRows = (sections) => (sections || []).flatMap(({ rows = [], visibleRows }) => visibleRows || rows);

/*>>> TREE DROP -> CANVAS-IDENTICAL PARAMS <<<*/
//--> the shared operand authority preserves saved ids and supplies the one valid dynamic slot when supported
export const firstFreeTreeSlot = (node, edges = []) => resolveConditionAttachHandle(node, edges);

const refused = (reason) => ({ valid: false, reason });

/**
 * Turn one tree drop into the exact params a Canvas edge drop produces, then run the shared canvas code.
 * payload = { source, sourceHandle, removeEdgeIds } from the drag source; slot = { target, targetHandle? }.
 * Move   = handleEdgesChange(remove old edge) then onConnect(params), synchronously in this one call.
 * Wire-in = onConnect(params). Nothing is written unless validateConnection accepts the params first.
 */
export const applyTreeDrop = ({ payload, slot, nodesById = {}, edges = [], graphWiring }) => {
  const moveEdgeId = (payload?.removeEdgeIds || [])[0];
  const oldEdge = moveEdgeId ? edges.find((edge) => edge.id === moveEdgeId) : null;
  if (moveEdgeId && !oldEdge) return refused(LINK_REFUSAL_TEXT.missing_edge);
  const { target } = slot;
  const targetNode = nodesById[target];
  const requestedTargetHandle = slot.targetHandle
    ? resolveTargetHandle(targetNode, slot.targetHandle)
    : firstFreeTreeSlot(targetNode, edges);
  //--> dropping an occurrence back on its own parent (or its own slot) changes nothing
  if (oldEdge && oldEdge.target === target
    && (!slot.targetHandle
      || resolveTargetHandle(
        targetNode,
        oldEdge.targetHandle,
        oldEdge.targetHandle === undefined || oldEdge.targetHandle === null || oldEdge.targetHandle === '' ? 0 : null,
      ) === requestedTargetHandle)) {
    return { valid: true, reason: '', noop: true };
  }
  if (oldEdge && [oldEdge.source, oldEdge.target].some((id) => nodesById[id]?.connectable === false)) {
    return refused(LINK_REFUSAL_TEXT.locked_node);
  }
  const sourceHandle = oldEdge ? oldEdge.sourceHandle : payload.sourceHandle;
  //* the refusal rules do not depend on the slot, so a forbidden drop reports its rule, not a missing slot
  const validation = graphWiring.validateConnection(
    { source: payload.source, sourceHandle, target, targetHandle: requestedTargetHandle },
    { ignoreEdgeIds: oldEdge ? [oldEdge.id] : [] },
  );
  if (!validation.valid) return validation;
  const targetHandle = requestedTargetHandle;
  if (!targetHandle) return refused(LINK_REFUSAL_TEXT.no_free_slot);
  const params = { source: payload.source, sourceHandle, target, targetHandle };
  if (oldEdge) graphWiring.handleEdgesChange([{ type: 'remove', id: oldEdge.id }]);
  graphWiring.onConnect(params);
  return { valid: true, reason: '', params };
};

export default function useTreeInteraction({
  sections = [],
  nodesById = {},
  filterRef,
  setCollapsedKeys,
  selectedId,
  onSelect,
  onLocate,
  edges = [],
  graphWiring,
  onDeleteNode,
  onCreateAndAttach,
  setStatus,
}) {
  const rows = useMemo(() => flattenRows(sections), [sections]);
  const visibleRows = useMemo(() => flattenVisibleRows(sections), [sections]);
  const rowsByKey = useMemo(() => Object.fromEntries(rows.map((row) => [row.key, row])), [rows]);
  const [activeKey, setActiveKey] = useState(visibleRows[0]?.key || null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  //--> the ONE row whose detail block is open (null = all closed). Opening a row closes the previous one; toggling
  //    the open row closes it. Only the info button and the `i` key toggle it; a row click never does.
  const [openDetailKey, setOpenDetailKey] = useState(null);
  const toggleDetail = useCallback((key) => {
    setOpenDetailKey((previous) => (previous === key ? null : key));
  }, []);

  const deleteRow = useCallback((row) => {
    if (row.connectable === false || row.deletable === false) {
      setStatus(LINK_REFUSAL_TEXT.locked_delete);
      return;
    }
    //* the canvas delete path (main's context-menu node delete): node removal + connected-edge removal sync
    const outcome = (onDeleteNode || graphWiring.deleteNode)(row.nodeId);
    if (outcome && outcome.ok === false) {
      setStatus(outcome.reason);
      return;
    }
    setStatus(`Deleted ${row.label}.`);
  }, [graphWiring, onDeleteNode, setStatus]);

  useEffect(() => {
    if (!visibleRows.some(({ key }) => key === activeKey)) setActiveKey(visibleRows[0]?.key || null);
  }, [activeKey, visibleRows]);

  const toggle = useCallback((key, forceExpanded) => {
    setCollapsedKeys((previous) => {
      const next = new Set(previous);
      const expanded = !next.has(key);
      const shouldExpand = forceExpanded == null ? !expanded : forceExpanded;
      if (shouldExpand) next.delete(key); else next.add(key);
      return next;
    });
  }, [setCollapsedKeys]);

  const rowFromElement = useCallback((element) => {
    const item = element?.closest?.('[data-tree-row-key]');
    return item ? rowsByKey[item.dataset.treeRowKey] : null;
  }, [rowsByKey]);

  const visibleItems = (root) => [...root.querySelectorAll('[role="treeitem"]')];
  const focusItem = (item) => {
    if (!item) return;
    setActiveKey(item.dataset.treeRowKey);
    item.focus();
  };

  const handleKeyDown = useCallback((event) => {
    if (isTextInput(event.target)) return;
    if (event.key === '/') {
      event.preventDefault();
      filterRef?.current?.focus();
      return;
    }
    const item = event.target?.closest?.('[role="treeitem"]');
    if (!item) return;
    const row = rowsByKey[item.dataset.treeRowKey];
    if (!row) return;
    const items = visibleItems(event.currentTarget);
    const index = items.indexOf(item);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const offset = event.key === 'ArrowDown' ? 1 : -1;
      focusItem(items[Math.max(0, Math.min(items.length - 1, index + offset))]);
      return;
    }
    if (event.key === 'ArrowRight' && item.hasAttribute('aria-expanded')) {
      event.preventDefault();
      if (item.getAttribute('aria-expanded') === 'false') toggle(row.key, true);
      else {
        const next = items[index + 1];
        if (next && Number(next.getAttribute('aria-level')) > Number(item.getAttribute('aria-level'))) focusItem(next);
      }
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (item.getAttribute('aria-expanded') === 'true') {
        toggle(row.key, false);
      } else {
        const level = Number(item.getAttribute('aria-level'));
        for (let candidate = index - 1; candidate >= 0; candidate -= 1) {
          if (Number(items[candidate].getAttribute('aria-level')) < level) {
            focusItem(items[candidate]);
            break;
          }
        }
      }
      return;
    }
    if (event.key === 'Enter') {
      //--> Enter on a control inside the row (chevron, details, Locate) activates that control natively (B6)
      if (event.target !== item && event.target.closest?.('button')) return;
      event.preventDefault();
      onSelect?.(row.node);
      return;
    }
    if (event.key.toLowerCase() === 'i' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      //--> only a row that has a details toggle (rows without detail content render none)
      if (!item.querySelector('[data-tree-details]')) return;
      event.preventDefault();
      toggleDetail(row.key);
      return;
    }
    if (event.key.toLowerCase() === 'l') {
      event.preventDefault();
      onLocate?.(row.node);
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      //--> Delete acts only on the focused row itself, never from a chip or control inside it (ARB-10)
      if (event.target !== item) return;
      event.preventDefault();
      event.stopPropagation();
      deleteRow(row);
    }
  }, [deleteRow, filterRef, onLocate, onSelect, rowsByKey, toggle, toggleDetail]);

  //--> Row chevrons toggle through ConditionRailTree's onToggleRow (and stop propagation), so clicks here are selection/Locate.
  //    A row click only selects (properties panel): it neither toggles the detail (the info button / `i` does) nor
  //    moves the canvas (only Locate, button or L, does).
  const handleClick = useCallback((event) => {
    const row = rowFromElement(event.target);
    if (!row) return;
    if (event.target.closest?.('[data-tree-details]')) {
      toggleDetail(row.key);
      return;
    }
    const locateButton = event.target.closest?.('[data-tree-locate]');
    if (locateButton) {
      onLocate?.(row.node);
      return;
    }
    //--> a chip selects its own node; a chip with no node (unwired constant, 'pick input') acts as the row line
    const chip = event.target.closest?.('[data-tree-chip]');
    const chipNode = chip ? nodesById[chip.dataset.nodeId] : null;
    if (chipNode) {
      onSelect?.(chipNode);
      return;
    }
    //--> the open detail block's own background is not the row line: it neither re-selects nor closes the block
    if (event.target.closest?.('[data-tree-detail]')) return;
    onSelect?.(row.node);
  }, [nodesById, onLocate, onSelect, rowFromElement, toggleDetail]);

  const handleDragStart = useCallback((event) => {
    //--> Resolve the nearest chip/row boundary so a locked or display-only chip cannot inherit a draggable
    //    row's source. Conversely, an unlocked operand chip inside a locked row remains its own valid source.
    const sourceElement = event.target.closest?.(
      '[data-tree-chip], [data-tree-row-key], [data-tree-drag-source], [data-tree-locked="true"]',
    );
    const locked = sourceElement?.dataset.treeLocked === 'true';
    const value = locked ? null : parseValue(sourceElement?.dataset.treeDragSource);
    if (!value || locked) {
      //--> only a real locked row/chip says "locked"; a text-selection drag elsewhere is left alone
      if (locked) {
        event.preventDefault();
        setStatus(LINK_REFUSAL_TEXT.locked_node);
      }
      return;
    }
    event.dataTransfer?.setData(TREE_NODE_MIME, JSON.stringify(value));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    //--> drop-target chips on the row lines show their full ring while this attribute is on the tree root
    event.currentTarget?.setAttribute?.('data-dragging', 'true');
  }, [setStatus]);

  const clearDragging = useCallback((event) => {
    event.currentTarget?.removeAttribute?.('data-dragging');
  }, []);

  const handleDragOver = useCallback((event) => {
    if (event.target.closest?.('[role="treeitem"], [data-tree-authoring-section]')) event.preventDefault();
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    clearDragging(event);
    const locked = event.target.closest?.('[data-tree-locked="true"]');
    if (locked) {
      setStatus(LINK_REFUSAL_TEXT.locked_node);
      return;
    }
    const source = parseValue(event.dataTransfer?.getData(TREE_NODE_MIME));
    const authoringItem = source?.source ? null : parseToolboxDropItem(event.dataTransfer);
    if (authoringItem) {
      const sectionElement = event.target.closest?.('[data-tree-authoring-section]');
      const targetElement = event.target.closest?.('[data-tree-rewire-target]');
      const target = sectionElement
        ? { sectionId: sectionElement.dataset.treeAuthoringSection }
        : (targetElement ? { nodeId: targetElement.dataset.treeRewireTarget } : null);
      if (!target) {
        setStatus(LINK_REFUSAL_TEXT.authoring_drop_target);
        return;
      }
      const response = onCreateAndAttach?.({ item: authoringItem, target })
        || { ok: false, reason: LINK_REFUSAL_TEXT.tree_authoring_unavailable };
      setStatus(response.ok || response.duplicate ? '' : (response.reason || LINK_REFUSAL_TEXT.item_add_failed));
      return;
    }
    if (!source?.source) {
      setStatus(LINK_REFUSAL_TEXT.dragged_operand_unavailable);
      return;
    }
    const slotElement = event.target.closest?.('[data-tree-drop-target]');
    let slot = parseValue(slotElement?.dataset.treeDropTarget);
    if (!slot) {
      const groupElement = event.target.closest?.('[data-tree-rewire-target]');
      if (!groupElement) {
        setStatus(LINK_REFUSAL_TEXT.connection_drop_target);
        return;
      }
      //--> a logic group or a condition row: applyTreeDrop fills its first free input (firstFreeTreeSlot)
      slot = { target: groupElement.dataset.treeRewireTarget };
    }
    const response = applyTreeDrop({ payload: source, slot, nodesById, edges, graphWiring });
    setStatus(response.valid ? '' : (response.reason || LINK_REFUSAL_TEXT.connection_not_allowed));
  }, [clearDragging, edges, graphWiring, nodesById, onCreateAndAttach, setStatus]);

  const rootProps = {
    onKeyDown: handleKeyDown,
    onClick: handleClick,
    onFocus: (event) => {
      const item = event.target.closest?.('[data-tree-row-key]');
      if (item) setActiveKey(item.dataset.treeRowKey);
    },
    onMouseOver: (event) => {
      const target = event.target.closest?.('[data-node-id]');
      setHoveredNodeId(target?.dataset.nodeId || null);
    },
    onMouseLeave: () => setHoveredNodeId(null),
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragEnd: clearDragging,
    onDrop: handleDrop,
  };

  return {
    activeKey,
    hoveredNodeId,
    sameNodeId: hoveredNodeId || selectedId || null,
    toggle,
    openDetailKey,
    toggleDetail,
    deleteRow,
    rootProps,
  };
}
